import type {
  Investigation,
  InvestigationCreateBody,
  InvestigationGeo,
  InvestigationGeoMode,
  InvestigationPatchBody,
} from '@/types/investigation'
import { apiClient } from './client'

/**
 * What a sync did to the snapshot: `synced` nodes refreshed from the graph,
 * `merged` nodes folded into the entities they were merged into since the
 * save (ADR-0079), `missing` nodes the graph no longer holds and no merge
 * explains (kept as saved). The three add up to `total`.
 */
export interface SyncPropertiesResult {
  investigation: Investigation
  synced: number
  merged: number
  missing: number
  total: number
}

export const investigationsApi = {
  list: (caseId: string) => apiClient.get<Investigation[]>(`/v1/cases/${caseId}/investigations`),

  get: (caseId: string, id: string) =>
    apiClient.get<Investigation>(`/v1/cases/${caseId}/investigations/${id}`),

  create: (caseId: string, body: InvestigationCreateBody) =>
    apiClient.post<Investigation>(`/v1/cases/${caseId}/investigations`, body),

  update: (caseId: string, id: string, patch: InvestigationPatchBody) =>
    apiClient.patch<Investigation>(`/v1/cases/${caseId}/investigations/${id}`, patch),

  remove: (caseId: string, id: string) =>
    apiClient.delete<void>(`/v1/cases/${caseId}/investigations/${id}`),

  syncProperties: (caseId: string, id: string) =>
    apiClient.post<SyncPropertiesResult>(
      `/v1/cases/${caseId}/investigations/${id}/sync-properties`,
      {}
    ),

  /**
   * How many entities of the saved copy a live merge folded away since the
   * save: the merges a Sync would apply (ADR-0079). No graph read behind it.
   */
  mergedEntities: (caseId: string, id: string) =>
    apiClient.get<{ count: number }>(`/v1/cases/${caseId}/investigations/${id}/merged-entities`),

  /**
   * Geo projection for a saved investigation's object set, built server-side
   * purely on `_geo_point` and scoped to the investigation's own nodes/edges.
   * `mode` defaults to `all`, returning the static pins, animated tracks, and
   * time events together in one round-trip (the three projections run
   * concurrently server-side). Single modes (`static`/`animated`/`time`) stay
   * available for callers that need just one.
   */
  geo: (caseId: string, id: string, mode: InvestigationGeoMode = 'all') =>
    apiClient.get<InvestigationGeo>(`/v1/cases/${caseId}/investigations/${id}/geo?mode=${mode}`),

  /**
   * Real-time geo projection for an arbitrary (unsaved) object set — a
   * transient/assistant-pushed view or a saved investigation with unsaved
   * exploration. Same engine + scoping as the stored projection; the map uses
   * this whenever there's no stored `geo` for the current object set.
   */
  projectGeo: (caseId: string, nodeIds: string[]) =>
    apiClient.post<InvestigationGeo>(`/v1/cases/${caseId}/geo/project`, { node_ids: nodeIds }),

  /**
   * Re-runs the search agent against the investigation's current state
   * (entities + notes + previous summary) and overwrites the
   * `summary`/`confidence`/`evidence` snapshot. Long-running — call from a
   * mutation with a loading state.
   */
  regenerateEvidence: (caseId: string, id: string) =>
    apiClient.post<Investigation>(
      `/v1/cases/${caseId}/investigations/${id}/regenerate-evidence`,
      {}
    ),
}
